import { reportHasOpenAuditFlag } from '../audit/openAuditFlag';
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteEntity } from '../../types/site';
import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type {
  PathToGoDecisionAnalysisSnapshot,
  PathToGoDecisionEvidenceSnapshot,
  PathToGoSystemDecisionSnapshot,
} from '../../types/pathToGoDecision';
import { PATH_TO_GO_DECISION_SCHEMA_VERSION_V2 } from '../../types/pathToGoDecision';
import { canonicalJson } from '../reportGeneration/reportContractHash';
import { hashProjectIntent } from '../sed/shouldBootstrap';
import {
  getPathToGoAnalysisPlan,
  type PathToGoState,
} from './pathToGoOrchestrator';

function reportTypeOf(report: SiteRagReport): ReportTypeId {
  return report.reportType ?? 'feasibility';
}

function reportsForPlan(
  reports: readonly SiteRagReport[],
  plan: ReturnType<typeof getPathToGoAnalysisPlan>,
): SiteRagReport[] {
  const byId = new Map(reports.map(report => [report.id, report]));
  const selected = new Map<ReportTypeId, SiteRagReport>();
  for (const item of plan) {
    if (item.decisionEvidenceSource === 'semantic-analysis') continue;
    const exact = item.calculationReportId
      ? byId.get(item.calculationReportId)
      : undefined;
    // Only legacy plan snapshots (which predate explicit calculation status)
    // may use latest-wins fallback. A new-plan `none` must never freeze an
    // unsafe failed/metadata-only attempt as decision evidence.
    const fallback = item.calculationStatus == null
      ? reports
          .filter(report => reportTypeOf(report) === item.type)
          .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))[0]
      : undefined;
    const report = exact ?? fallback;
    if (report) selected.set(item.type, report);
  }
  return [...selected.values()].sort((left, right) =>
    reportTypeOf(left).localeCompare(reportTypeOf(right)));
}

function analysesForPlan(state: PathToGoState): PathToGoDecisionAnalysisSnapshot[] {
  return getPathToGoAnalysisPlan(state)
    .filter(item =>
      item.decisionEvidenceSource === 'semantic-analysis'
      && item.analysisId
      && item.analysisInputHash
      && item.analysisContentHash
      && item.analysisSnapshotPath
      && item.analysisCreatedAt
      && item.analysisInput)
    .map(item => ({
      analysisId: item.analysisId!,
      domain: item.analysisInput!.domain,
      inputHash: item.analysisInputHash!,
      contentHash: item.analysisContentHash!,
      snapshotPath: item.analysisSnapshotPath!,
      createdAt: item.analysisCreatedAt!,
      input: structuredClone(item.analysisInput!),
    }))
    .sort((left, right) => left.domain.localeCompare(right.domain));
}

function systemSnapshot(state: PathToGoState): PathToGoSystemDecisionSnapshot {
  const strictBand = state.decisionRollup
    ? state.decisionRollup.band
    : state.verdict.band;
  const band = strictBand === 'conditional-high'
    ? 'conditionalHigh'
    : strictBand === 'no-go'
      ? 'noGo'
      : strictBand ?? 'withheld';
  return {
    band,
    label: state.decisionRollup?.label ?? state.verdict.bandLabel,
    composite: state.verdict.composite,
    confidenceTag: state.verdict.confidenceTag,
    dataAdequate: state.decisionRollup
      ? state.decisionRollup.adequacy.adequate
      : state.verdict.dataAdequate,
    adequacyReason: state.decisionRollup
      ? state.decisionRollup.adequacy.reason
      : state.verdict.adequacyReason,
    coverageRatio: state.decisionRollup
      ? state.decisionRollup.adequacy.coverageRatio
      : state.adequacyGate.coverageRatio,
  };
}

/**
 * Positive decisions require the complete Define → Analyze → Verify record.
 * NO-GO and DEFER remain available early and are separately governed by the
 * approval workflow.
 */
export function pathToGoPositiveDecisionAllowed(state: PathToGoState): boolean {
  const prerequisitePhases = state.phases.filter(phase => phase.id !== 'decide');
  return Boolean(
    state.adequacyGate.adequate
      && state.generationImpactApplicability.status !== 'undecided'
      && state.generationImpactApplicability.status !== 'conflict'
      && state.generationImpactApplicability.strategyDeclared
      && prerequisitePhases.every(phase => phase.status === 'cleared')
      && getPathToGoAnalysisPlan(state)
        .filter(item => item.role === 'required')
        .every(item => item.satisfiesGo === true),
  );
}

/** Freeze the exact live inputs used by the decision cockpit. */
export function buildPathToGoDecisionEvidenceSnapshot(input: {
  site: Pick<SiteEntity, 'sedId' | 'sedVersion'> & Partial<Pick<SiteEntity, 'demonstrationProvenance'>>;
  intent: ProjectIntent | undefined;
  reports: readonly SiteRagReport[];
  state: PathToGoState;
  capturedAt?: string;
}): PathToGoDecisionEvidenceSnapshot {
  const capturedAt = input.capturedAt ?? new Date().toISOString();
  const analysisPlan = getPathToGoAnalysisPlan(input.state);
  const sourceReports = reportsForPlan(input.reports, analysisPlan);
  const planByType = new Map(analysisPlan.map(item => [item.type, item]));
  const sourceAnalyses = analysesForPlan(input.state);
  const testFit = sourceReports.find(report => reportTypeOf(report) === 'test-fit');
  const projectIntent = input.intent
    ? structuredClone(input.intent)
    : null;

  const base: PathToGoDecisionEvidenceSnapshot = {
    capturedAt,
    ...(input.site.demonstrationProvenance ? { decisionScope: {
      kind: 'hypothetical-scenario' as const,
      scenarioId: input.site.demonstrationProvenance.scenarioId,
      realWorldEligible: false as const,
    } } : {}),
    intentHash: hashProjectIntent(projectIntent),
    projectIntent,
    sedId: input.site.sedId ?? null,
    sedVersion: input.site.sedVersion ?? null,
    generationImpactApplicability: input.state.generationImpactApplicability.status,
    btmStrategy: input.state.generationImpactApplicability.btmStrategy ?? null,
    strategyDeclared: input.state.generationImpactApplicability.strategyDeclared,
    requiredReportTypes: analysisPlan
      .filter(item => item.role === 'required')
      .map(item => item.type)
      .sort(),
    positiveDecisionGateSatisfied: pathToGoPositiveDecisionAllowed(input.state),
    system: systemSnapshot(input.state),
    reports: sourceReports.map(report => ({
      id: report.id,
      reportType: reportTypeOf(report),
      version: report.version ?? 1,
      minorVersion: report.minorVersion ?? 0,
      createdAt: report.createdAt,
      ...(planByType.get(reportTypeOf(report))?.calculationStatus === 'saved'
        ? { savedBasis: true as const }
        : {}),
      // AUDIT-ADVISORY (2026-08-22): a GO recorded over audit-flagged
      // evidence must SAY so permanently. Conditional spread keeps every
      // historical record byte-identical on rebuild (the old policy could
      // not record GO over held reports, so no old record carries this).
      //
      // THE AUDIT KIND DELIBERATELY DOES NOT TRAVEL HERE (round 9). The plan
      // item gained `auditKind` so the surfaces that put the flag in a SENTENCE
      // can say whether anything is itemised; this is not a surface. It is a
      // RECORD IDENTITY: `pathToGoDecisionEvidenceStillCurrent` compares the
      // canonical JSON of a submitted envelope against a freshly rebuilt one,
      // so a new key here re-keys every approval captured before it — and it
      // would re-key them on a value that is RE-EVALUATED live
      // (`reevaluateStampedHoldFindings`), meaning the same sealed evidence
      // could rebuild to a different kind after a rule change and silently
      // invalidate a recorded decision. What the record must state is that the
      // evidence was flagged when the decision was made, and that is what the
      // flag states. Locked in decisionSnapshot.test.ts.
      ...(reportHasOpenAuditFlag(report) ? { auditRecommended: true } : {}),
      ...(report.sedId ? { sedId: report.sedId } : {}),
      ...(report.sedVersionUsed != null
        ? { sedVersionUsed: report.sedVersionUsed }
        : {}),
      ...(report.testFitAuthorityRef
        ? { testFitAuthorityRef: structuredClone(report.testFitAuthorityRef) }
        : {}),
    })),
    ...(testFit?.testFitAuthorityRef
      ? { testFitAuthorityRef: structuredClone(testFit.testFitAuthorityRef) }
      : {}),
  };
  if (sourceAnalyses.length === 0) return base;
  return {
    ...base,
    schemaVersion: PATH_TO_GO_DECISION_SCHEMA_VERSION_V2,
    analyses: sourceAnalyses,
  };
}

/**
 * Approval must apply to the evidence revision that was actually submitted.
 * `capturedAt` is deliberately excluded: rebuilding an otherwise identical
 * live envelope at review time should compare equal.
 */
export function pathToGoDecisionEvidenceStillCurrent(
  submitted: PathToGoDecisionEvidenceSnapshot,
  current: PathToGoDecisionEvidenceSnapshot,
): boolean {
  const { capturedAt: _submittedAt, ...submittedAuthority } = submitted;
  const { capturedAt: _currentAt, ...currentAuthority } = current;
  return canonicalJson(submittedAuthority) === canonicalJson(currentAuthority);
}
